import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import FacilityRow from "./FacilityRow";

// THE single column template for the facility grid (design §3 rule 3).
//
// Every franchise group renders its own <Table>, so without pinned widths the columns drift
// between groups and the page stops reading as one continuous grid. `tableLayout: fixed` plus
// this shared <colgroup> guarantees alignment. Widths are defined here and nowhere else.
//
// The Actions column is part of the final layout but is only rendered once a slice supplies
// action handlers (A3 onwards) -- an always-empty column with a header would be worse than none.
// The Sites cell is a NEUTRAL count summary only ("No sites" / "1 site" / "3 sites"). The detailed
// asset information -- fields, space, indoor/outdoor -- lives in the Manage Sites dialog (A6), not
// in this grid, so the Site grain stays administrable without consuming grid width.
// Actions is deliberately the second-widest column: the approved labels are full phrases
// ("Edit Details", "Edit Ownership") and they must sit on ONE line -- letting them wrap doubles
// every row's height and destroys the compact grid this layout exists for.
// Actions holds three full-phrase buttons and must fit them on ONE line at 1280px. Measured at
// A6: the three labels need 291px of a 286px content box at 26% -- a 5px miss that made every
// label wrap and pushed rows from 44px to 66px. 28% restores single-line rows with ~30px slack.
// This column is now at its practical limit: a fourth action, or wider BDM content from the
// Multi-BDM workstream, should trigger the agreed overflow-menu hatch (Manage Sites moves first).
export const FACILITY_COLUMNS = [
  { id: "name", label: "Facility", width: "19%", align: "left" },
  { id: "status", label: "Status", width: "8%", align: "left" },
  { id: "bdm", label: "Effective BDM", width: "22%", align: "left" },
  { id: "pacesetter", label: "PaceSetter", width: "9%", align: "center" },
  { id: "nll", label: "NLL", width: "6%", align: "center" },
  { id: "sites", label: "Sites", width: "8%", align: "center" },
  { id: "actions", label: "Actions", width: "28%", align: "right" },
];

// The flat Facilities lens (A7) shows the same columns plus the owning franchise, since rows are
// no longer grouped under a band. Separate widths rather than a shared template: the franchise
// column has to come from somewhere, and squeezing it out of the grouped widths would push the
// actions column back under its measured minimum (see the A6 note above).
// Actions carries the SAME three buttons here as in the grouped view, so it needs the same ~290px
// of content box (measured in A6). Budgeting it 21% left only 225px and pushed the buttons 49px
// left into the Sites column -- the franchise column has to be funded from the other text columns,
// not from Actions.
export const FLAT_FACILITY_COLUMNS = [
  { id: "franchise", label: "Franchise", width: "13%", align: "left" },
  { id: "name", label: "Facility", width: "14%", align: "left" },
  { id: "status", label: "Status", width: "7%", align: "left" },
  { id: "bdm", label: "Effective BDM", width: "18%", align: "left" },
  { id: "pacesetter", label: "PaceSetter", width: "7%", align: "center" },
  { id: "nll", label: "NLL", width: "5%", align: "center" },
  { id: "sites", label: "Sites", width: "8%", align: "center" },
  { id: "actions", label: "Actions", width: "28%", align: "right" },
];

export function visibleFacilityColumns(showActions, showFranchise) {
  const template = showFranchise ? FLAT_FACILITY_COLUMNS : FACILITY_COLUMNS;
  return template.filter((column) => column.id !== "actions" || showActions);
}

export default function FacilityGrid(props) {
  const facilities = props.facilities || [];
  const showActions = Boolean(props.showActions);
  const bdmOptions = props.bdmOptions;
  const onSaveOwnership = props.onSaveOwnership;
  const onSaveDetails = props.onSaveDetails;
  const onSaveSite = props.onSaveSite;
  const onDeleteSite = props.onDeleteSite;
  const onSaved = props.onSaved;

  const columns = visibleFacilityColumns(showActions);

  const colElements = columns.map((column) => <col key={column.id} style={{ width: column.width }} />);

  const headCells = columns.map((column) => (
    <TableCell key={column.id} align={column.align} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
      {column.label}
    </TableCell>
  ));

  const rows = facilities.map((facility) => (
    <FacilityRow
      key={facility.id}
      facility={facility}
      showActions={showActions}
      bdmOptions={bdmOptions}
      onSaveOwnership={onSaveOwnership}
      onSaveDetails={onSaveDetails}
      onSaveSite={onSaveSite}
      onDeleteSite={onDeleteSite}
      onSaved={onSaved}
    />
  ));

  return (
    <Table size="small" sx={{ tableLayout: "fixed" }}>
      <colgroup>{colElements}</colgroup>
      <TableHead>
        <TableRow>{headCells}</TableRow>
      </TableHead>
      <TableBody>{rows}</TableBody>
    </Table>
  );
}
